import React, { useState } from 'react'
import styled from 'styled-components'
import { Grid } from '@gnomad/ui'
import { getConsequenceColor } from '../VariantList/variantTableColumns'
import { getLabelForConsequenceTerm } from '../vepConsequences'
import { ColorMarker, RightArrow } from '../UserInterface'
import { renderPvalueCell } from '../PhenotypeList/Utils'
import { UnifiedContextMenu } from '../components/UnifiedContextMenu'
import { ContextMenuTrigger } from '../components/ContextMenuTrigger'
import { useContextMenuNavigation } from '../hooks/useContextMenuNavigation'
import { useAppNavigation } from '../hooks/useAppNavigation'

const DescriptionContainer = styled.span`
  overflow: hidden;
  max-width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const VariantLinkRenderer = ({ row }: any) => {
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null)
  const navigate = useContextMenuNavigation()
  const { goToVariant } = useAppNavigation()

  return (
    <>
      <ContextMenuTrigger
        className="grid-cell-content"
        menuOpen={menu !== null}
        onPrimaryAction={() =>
          goToVariant(row.variant_id, {
            destination: 'phewas',
            analysisId: row.top_phenotype,
            geneId: row.gene_id || null,
            regionId: null,
            resultIndex: 'variant-phewas',
            resultsOnly: true,
          })
        }
        onOpenMenu={setMenu}
        title="Open variant; press Shift+F10 for more actions"
      >
        <DescriptionContainer>{row.variant_id}</DescriptionContainer>
      </ContextMenuTrigger>
      {menu && (
        <UnifiedContextMenu
          x={menu.x}
          y={menu.y}
          title={`VARIANT: ${row.variant_id}`}
          targets={[{ label: 'Variant PheWAS', resultIndex: 'variant-phewas' }]}
          onNavigate={(mode, target) => {
            navigate('variant', row.variant_id, mode, target)
            setMenu(null)
          }}
          onCopy={() => {
            navigator.clipboard.writeText(row.variant_id)
            setMenu(null)
          }}
          copyLabel="Copy Variant ID"
          onClose={() => setMenu(null)}
        />
      )}
    </>
  )
}

export const TopVariantsTable = ({ variants, onVariantClick, categoryColors }: any) => {
  const [sortKey, setSortKey] = useState('num_associations')
  const [sortOrder, setSortOrder] = useState<'ascending' | 'descending'>('descending')

  const columns = [
    {
      key: 'variant_id',
      heading: 'Variant ID',
      isSortable: true,
      minWidth: 150,
      grow: 1,
      render: (row: any) => <VariantLinkRenderer row={row} />,
    },
    {
      key: 'gene_symbol',
      heading: 'Gene',
      isSortable: true,
      minWidth: 100,
      grow: 0,
      render: (row: any) => row.gene_symbol || '-',
    },
    {
      key: 'consequence',
      heading: 'Consequence',
      isSortable: true,
      minWidth: 150,
      grow: 0,
      render: (row: any) => (
        <span className="grid-cell-content">
          <ColorMarker color={getConsequenceColor(row.consequence)} />
          {row.consequence ? getLabelForConsequenceTerm(row.consequence) : 'N/A'}
        </span>
      ),
    },
    {
      key: 'top_phenotype_category',
      heading: 'Category',
      isSortable: true,
      minWidth: 130,
      grow: 0,
      render: (row: any) => {
        const cat = row.top_phenotype_category || 'Unknown'
        const color = categoryColors?.get(cat) || '#999'
        return (
          <span className="grid-cell-content">
            <ColorMarker color={color} />
            <DescriptionContainer title={cat}>{cat}</DescriptionContainer>
          </span>
        )
      },
    },
    {
      key: 'top_phenotype_description',
      heading: 'Top Phenotype',
      isSortable: true,
      minWidth: 250,
      grow: 1,
      render: (row: any) => {
        if (row.matched_phenotype_description && row.matched_phenotype !== row.top_phenotype) {
          return (
            <DescriptionContainer title={`Matched: ${row.matched_phenotype_description}\nTop: ${row.top_phenotype_description}`}>
              <span style={{ fontWeight: 500 }}>{row.matched_phenotype_description}</span>
              <span style={{ color: 'var(--theme-text-muted, #999)', fontSize: '11px' }}>{' '}(top: {row.top_phenotype_description})</span>
            </DescriptionContainer>
          )
        }
        return (
          <DescriptionContainer title={row.top_phenotype_description}>
            {row.top_phenotype_description}
          </DescriptionContainer>
        )
      },
    },
    {
      key: 'top_pvalue',
      heading: 'Top P-Value',
      isSortable: true,
      minWidth: 100,
      grow: 0,
      render: renderPvalueCell('variant', 'pvalue'),
    },
    {
      key: 'num_associations',
      heading: '# Significant',
      tooltip: 'Number of phenotypes this variant is significantly associated with',
      isSortable: true,
      minWidth: 100,
      grow: 0,
      render: (row: any) => row.num_associations.toLocaleString(),
    },
    {
      key: 'show',
      heading: '',
      isRowHeader: true,
      isSortable: false,
      minWidth: 50,
      grow: 0,
      render: (row: any) => {
        return <RightArrow onClick={() => onVariantClick(row)} ariaLabel={`Open ${row.variant_id} details`} />
      },
    },
  ]

  const handleRequestSort = (newSortKey: string) => {
    if (newSortKey === sortKey) {
      setSortOrder(sortOrder === 'ascending' ? 'descending' : 'ascending')
    } else {
      setSortKey(newSortKey)
      setSortOrder('ascending')
    }
  }

  const sortedData = [...variants].sort((a: any, b: any) => {
    let valA = a[sortKey]
    let valB = b[sortKey]

    if (valA == null) valA = sortOrder === 'ascending' ? Infinity : -Infinity
    if (valB == null) valB = sortOrder === 'ascending' ? Infinity : -Infinity

    if (typeof valA === 'string' && typeof valB === 'string') {
      return sortOrder === 'ascending' ? valA.localeCompare(valB) : valB.localeCompare(valA)
    }

    return sortOrder === 'ascending' ? (valA > valB ? 1 : -1) : valA < valB ? 1 : -1
  })

  return (
    <div style={{ width: '100%', height: 'calc(100vh - 200px)' }}>
      <Grid
        columns={columns}
        data={sortedData}
        numRowsRendered={20}
        rowKey={(row: any) => row.variant_id}
        sortKey={sortKey}
        sortOrder={sortOrder}
        onRequestSort={handleRequestSort}
      />
    </div>
  )
}
